import React from 'react';
import DatePicker from 'react-datepicker';

interface OverviewCsvModalProps {
  showCsvModal: boolean;
  setShowCsvModal: (v: boolean) => void;
  csvStartDate: Date | null;
  setCsvStartDate: (v: Date | null) => void;
  csvEndDate: Date | null;
  setCsvEndDate: (v: Date | null) => void;
  reportType: 'accessed' | 'searched';
  setReportType: (v: 'accessed' | 'searched') => void;
  handleCsvDownload: () => void;
  isCSVLoading: boolean;
}

const renderCsvHeader = (
  { date, decreaseMonth, increaseMonth, changeYear, changeMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }: any
) => (
  <div className="datepicker-custom-header">
    <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}
      className="datepicker-nav-button" title="Previous month">{'<'}</button>
    <div className="datepicker-selectors">
      <select value={date.getMonth()}
        onChange={({ target: { value } }) => changeMonth(Number.parseInt(value))}
        className="datepicker-month-select">
        {Array.from({ length: 12 }, (_, i) => (
          <option key={i} value={i}>
            {new Date(0, i).toLocaleString('default', { month: 'long' })}
          </option>
        ))}
      </select>
      <select value={date.getFullYear()}
        onChange={({ target: { value } }) => changeYear(Number.parseInt(value))}
        className="datepicker-year-select">
        {Array.from({ length: 10 }, (_, i) => {
          const year = new Date().getFullYear() - 5 + i;
          return (<option key={year} value={year}>{year}</option>);
        })}
      </select>
    </div>
    <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}
      className="datepicker-nav-button" title="Next month">{'>'}</button>
  </div>
);

const getCsvDayClassName = (
  date: Date, csvStartDate: Date | null, csvEndDate: Date | null, minDateOverride?: Date | null
) => {
  const today = new Date();
  const minDate = minDateOverride || new Date('2020-01-01');
  const maxDate = new Date();
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const compareToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const compareMin = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  const compareMax = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  const isToday = compareDate.getTime() === compareToday.getTime();
  const isStart = csvStartDate && compareDate.getTime() === new Date(csvStartDate.getFullYear(), csvStartDate.getMonth(), csvStartDate.getDate()).getTime();
  const isEnd = csvEndDate && compareDate.getTime() === new Date(csvEndDate.getFullYear(), csvEndDate.getMonth(), csvEndDate.getDate()).getTime();
  const isDisabled = compareDate < compareMin || compareDate > compareMax;
  const isInRange = csvStartDate && csvEndDate && compareDate >= new Date(csvStartDate.getFullYear(), csvStartDate.getMonth(), csvStartDate.getDate()) && compareDate <= new Date(csvEndDate.getFullYear(), csvEndDate.getMonth(), csvEndDate.getDate());
  if (isDisabled) return 'react-datepicker__day--disabled-custom';
  if (isToday) return 'react-datepicker__day--today-custom';
  if (isStart || isEnd) return 'react-datepicker__day--selected-custom';
  if (isInRange) return 'react-datepicker__day--in-range-custom';
  return null;
};

const OverviewCsvModal: React.FC<OverviewCsvModalProps> = ({
  showCsvModal, setShowCsvModal,
  csvStartDate, setCsvStartDate, csvEndDate, setCsvEndDate,
  reportType, setReportType,
  handleCsvDownload, isCSVLoading,
}) => {
  if (!showCsvModal) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Download Report</h2>
          <button className="modal-close-button" onClick={() => setShowCsvModal(false)} title="Close modal">✕</button>
        </div>
        <div className="modal-body">
          <div className="modal-section">
            <h3 className="modal-section-title"><label>Report Type:</label></h3>
            <div className="radio-group">
              <label className="radio-label">
                <input type="radio" name="reportType" value="accessed"
                  checked={reportType === "accessed"} onChange={() => setReportType("accessed")}
                  className="radio-input" />
                <span className="radio-text">User Logins</span>
              </label>
              <label className="radio-label">
                <input type="radio" name="reportType" value="searched"
                  checked={reportType === "searched"} onChange={() => setReportType("searched")}
                  className="radio-input" />
                <span className="radio-text">Search History</span>
              </label>
            </div>
          </div>
          <div className="modal-section">
            <h4 className="modal-section-title"><label>Select Date Range:</label></h4>
            <div className="csv-date-picker-container">
              <DatePicker
                selected={csvStartDate}
                onChange={(date: Date | null) => setCsvStartDate(date)}
                selectsStart startDate={csvStartDate} endDate={csvEndDate}
                minDate={new Date('2020-01-01')} maxDate={new Date()}
                dateFormat="MMMM d, yyyy" isClearable placeholderText="Start Date"
                popperPlacement="bottom-start" className="modal-datepicker"
                dayClassName={(date) => getCsvDayClassName(date, csvStartDate, csvEndDate)}
                renderCustomHeader={renderCsvHeader}
              />
              <DatePicker
                selected={csvEndDate}
                onChange={(date: Date | null) => setCsvEndDate(date)}
                selectsEnd startDate={csvStartDate} endDate={csvEndDate}
                minDate={csvStartDate || new Date('2020-01-01')} maxDate={new Date()}
                dateFormat="MMMM d, yyyy" isClearable placeholderText="End Date"
                popperPlacement="bottom-start" className="modal-datepicker"
                dayClassName={(date) => getCsvDayClassName(date, csvStartDate, csvEndDate, csvStartDate)}
                renderCustomHeader={renderCsvHeader}
              />
            </div>
            <p className="modal-note"><sup>*</sup>Select date range for report generation</p>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={() => handleCsvDownload()}
            disabled={!csvStartDate || !csvEndDate || isCSVLoading}
            className="modal-button modal-button-primary"
            title={!csvStartDate || !csvEndDate ? "Please select both start and end dates" : "Download CSV report"}>
            {isCSVLoading ? 'Downloading...' : 'Download CSV'}
          </button>
          <button onClick={() => setShowCsvModal(false)}
            className="modal-button modal-button-secondary" title="Close without downloading">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default OverviewCsvModal;
